/**
 * Toast/Toaster System
 *
 * A complete toast notification system with:
 * - Programmatic control via useToast hook
 * - Auto-close functionality with configurable duration
 * - Smooth enter/exit animations
 * - Flexible placement (9 positions)
 * - Persistent toast option
 * - Manual close override
 *
 * @example
 * // Setup: Wrap your app with ToastProvider
 * import { ToastProvider } from 'src/components/ui/toast';
 *
 * function App() {
 *   return (
 *     <ToastProvider>
 *       <YourApp />
 *     </ToastProvider>
 *   );
 * }
 *
 * @example
 * // Usage: Use the useToast hook
 * import { useToast } from 'src/components/ui/toast';
 *
 * function MyComponent() {
 *   const { showToast, hideToast, hideAllToasts } = useToast();
 *
 *   const handleSuccess = () => {
 *     showToast({
 *       title: 'Success!',
 *       description: 'Your changes have been saved.',
 *       color: 'success',
 *       duration: 5000, // Auto-close after 5 seconds
 *     });
 *   };
 *
 *   const handlePersistent = () => {
 *     showToast({
 *       title: 'Important Notice',
 *       description: 'This will stay until manually closed.',
 *       color: 'warning',
 *       persistent: true, // Won't auto-close
 *     });
 *   };
 *
 *   return <button onClick={handleSuccess}>Save</button>;
 * }
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from 'src/utils/utils';
import { Alert, type AlertProps, type AlertAction } from './alert';

// ============================================
// TYPES
// ============================================

export type ToastPlacement =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'center-left'
  | 'center'
  | 'center-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface ToastOptions extends Omit<AlertProps, 'onClose'> {
  /**
   * Unique identifier for the toast.
   * Auto-generated if not provided.
   */
  id?: string;
  /**
   * Duration in milliseconds before auto-close.
   * Set to 0 or use `persistent: true` to disable auto-close.
   * @default 5000 (5 seconds)
   */
  duration?: number;
  /**
   * If true, the toast won't auto-close.
   * User must manually close it.
   * @default false
   */
  persistent?: boolean;
  /**
   * Callback when toast is closed (either manually or auto).
   */
  onClose?: () => void;
  /**
   * Placement of the toast on screen.
   * Can be overridden per-toast or set globally on ToastProvider.
   */
  placement?: ToastPlacement;
}

interface Toast extends ToastOptions {
  id: string;
  isExiting: boolean;
}

interface ToastContextValue {
  /**
   * Show a new toast notification.
   * @returns The toast ID for manual control.
   */
  showToast: (options: ToastOptions) => string;
  /**
   * Hide a specific toast by ID.
   */
  hideToast: (id: string) => void;
  /**
   * Hide all active toasts.
   */
  hideAllToasts: () => void;
  /**
   * Update an existing toast.
   */
  updateToast: (id: string, options: Partial<ToastOptions>) => void;
}

interface ToastProviderProps {
  children: React.ReactNode;
  /**
   * Default placement for all toasts.
   * @default 'top-right'
   */
  defaultPlacement?: ToastPlacement;
  /**
   * Default duration for all toasts in milliseconds.
   * @default 5000
   */
  defaultDuration?: number;
  /**
   * Maximum number of toasts to show at once.
   * Oldest toasts will be removed when limit is reached.
   * @default 5
   */
  maxToasts?: number;
}

// ============================================
// CONSTANTS
// ============================================

const ANIMATION_DURATION = 300; // ms

const placementClasses: Record<ToastPlacement, string> = {
  'top-left': 'tw:top-l tw:left-l tw:items-start',
  'top-center': 'tw:top-l tw:left-1/2 tw:-translate-x-1/2 tw:items-center',
  'top-right': 'tw:top-l tw:right-l tw:items-end',
  'center-left': 'tw:top-1/2 tw:left-l tw:-translate-y-1/2 tw:items-start',
  center:
    'tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:items-center',
  'center-right': 'tw:top-1/2 tw:right-l tw:-translate-y-1/2 tw:items-end',
  'bottom-left': 'tw:bottom-l tw:left-l tw:items-start',
  'bottom-center':
    'tw:bottom-l tw:left-1/2 tw:-translate-x-1/2 tw:items-center',
  'bottom-right': 'tw:bottom-l tw:right-l tw:items-end'
};

// Animation classes based on placement
const getEnterAnimation = (placement: ToastPlacement): string => {
  if (placement.includes('top')) return 'tw:animate-slide-in-down';
  if (placement.includes('bottom')) return 'tw:animate-slide-in-up';
  if (placement.includes('left')) return 'tw:animate-slide-in-left';
  if (placement.includes('right')) return 'tw:animate-slide-in-right';
  return 'tw:animate-fade-in';
};

const getExitAnimation = (placement: ToastPlacement): string => {
  if (placement.includes('top')) return 'tw:animate-slide-out-up';
  if (placement.includes('bottom')) return 'tw:animate-slide-out-down';
  if (placement.includes('left')) return 'tw:animate-slide-out-left';
  if (placement.includes('right')) return 'tw:animate-slide-out-right';
  return 'tw:animate-fade-out';
};

// ============================================
// CONTEXT
// ============================================

const ToastContext = React.createContext<ToastContextValue | null>(null);

// ============================================
// HOOK
// ============================================

export const useToast = (): ToastContextValue => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// ============================================
// TOAST ITEM COMPONENT
// ============================================

interface ToastItemProps {
  toast: Toast;
  onClose: (id: string) => void;
  placement: ToastPlacement;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose, placement }) => {
  const {
    id,
    isExiting,
    duration,
    persistent,
    onClose: toastOnClose,
    primaryAction,
    secondaryAction,
    ...alertProps
  } = toast;

  // Handle close with callback
  const handleClose = React.useCallback(() => {
    toastOnClose?.();
    onClose(id);
  }, [id, onClose, toastOnClose]);

  // Wrap actions to close toast after action
  const wrappedPrimaryAction: AlertAction | undefined = primaryAction
    ? {
        ...primaryAction,
        onClick: () => {
          primaryAction.onClick();
          handleClose();
        }
      }
    : undefined;

  const wrappedSecondaryAction: AlertAction | undefined = secondaryAction
    ? {
        ...secondaryAction,
        onClick: () => {
          secondaryAction.onClick();
          handleClose();
        }
      }
    : undefined;

  // Determine animation class
  const animationClass = isExiting
    ? getExitAnimation(placement)
    : getEnterAnimation(placement);

  return (
    <div
      className={cn('tw:transition-all', animationClass)}
      style={{ animationDuration: `${ANIMATION_DURATION}ms` }}
    >
      <Alert
        {...alertProps}
        onClose={handleClose}
        primaryAction={wrappedPrimaryAction}
        secondaryAction={wrappedSecondaryAction}
      />
    </div>
  );
};

// ============================================
// TOAST CONTAINER COMPONENT
// ============================================

interface ToastContainerProps {
  toasts: Toast[];
  placement: ToastPlacement;
  onClose: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  placement,
  onClose
}) => {
  // Filter toasts for this specific placement
  // Each toast has its own placement stored, so we match exactly
  const placementToasts = toasts.filter((t) => t.placement === placement);

  if (placementToasts.length === 0) return null;

  return (
    <div
      className={cn(
        'tw:fixed tw:z-99999 tw:flex tw:flex-col tw:gap-m tw:pointer-events-none',
        placementClasses[placement]
      )}
    >
      {placementToasts.map((toast) => (
        <div key={toast.id} className="tw:pointer-events-auto">
          <ToastItem
            toast={toast}
            onClose={onClose}
            placement={toast.placement}
          />
        </div>
      ))}
    </div>
  );
};

// ============================================
// PROVIDER COMPONENT
// ============================================

let toastCounter = 0;
const generateId = () => `toast-${++toastCounter}-${Date.now()}`;

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  defaultPlacement = 'top-right',
  defaultDuration = 5000,
  maxToasts = 5
}) => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const timersRef = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Use refs to avoid stale closures in callbacks
  const defaultDurationRef = React.useRef(defaultDuration);
  const defaultPlacementRef = React.useRef(defaultPlacement);
  const maxToastsRef = React.useRef(maxToasts);

  // Keep refs in sync with props
  React.useEffect(() => {
    defaultDurationRef.current = defaultDuration;
    defaultPlacementRef.current = defaultPlacement;
    maxToastsRef.current = maxToasts;
  }, [defaultDuration, defaultPlacement, maxToasts]);

  // Cleanup timers on unmount
  React.useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  // Start exit animation and remove toast after animation
  const startExitAnimation = React.useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, isExiting: true } : t))
    );

    // Remove after animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, ANIMATION_DURATION);
  }, []);

  // Hide a specific toast
  const hideToast = React.useCallback(
    (id: string) => {
      // Clear any existing timer
      const timer = timersRef.current.get(id);
      if (timer) {
        clearTimeout(timer);
        timersRef.current.delete(id);
      }

      startExitAnimation(id);
    },
    [startExitAnimation]
  );

  // Hide all toasts
  const hideAllToasts = React.useCallback(() => {
    // Clear all timers
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();

    // Start exit animation for all
    setToasts((prev) => prev.map((t) => ({ ...t, isExiting: true })));

    // Remove all after animation
    setTimeout(() => {
      setToasts([]);
    }, ANIMATION_DURATION);
  }, []);

  // Show a new toast
  const showToast = React.useCallback(
    (options: ToastOptions): string => {
      const id = options.id || generateId();
      // Use provided duration, fallback to ref (which holds current defaultDuration)
      const duration = options.duration ?? defaultDurationRef.current;
      const persistent = options.persistent ?? false;
      // Use provided placement, fallback to ref (which holds current defaultPlacement)
      const placement = options.placement || defaultPlacementRef.current;

      const newToast: Toast = {
        ...options,
        id,
        isExiting: false,
        duration, // Store the resolved duration on the toast
        placement // Store the resolved placement on the toast
      };

      setToasts((prev) => {
        // Remove oldest toasts if exceeding max
        let updated = [...prev, newToast];
        const currentMaxToasts = maxToastsRef.current;
        if (updated.length > currentMaxToasts) {
          const toRemove = updated.slice(0, updated.length - currentMaxToasts);
          toRemove.forEach((t) => {
            const timer = timersRef.current.get(t.id);
            if (timer) {
              clearTimeout(timer);
              timersRef.current.delete(t.id);
            }
          });
          updated = updated.slice(-currentMaxToasts);
        }
        return updated;
      });

      // Set auto-close timer if not persistent
      if (!persistent && duration > 0) {
        const timer = setTimeout(() => {
          startExitAnimation(id);
          timersRef.current.delete(id);
        }, duration);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [startExitAnimation] // Removed dependencies that are now accessed via refs
  );

  // Update an existing toast
  const updateToast = React.useCallback(
    (id: string, options: Partial<ToastOptions>) => {
      setToasts((prev) => {
        const updatedToasts = prev.map((t) =>
          t.id === id ? { ...t, ...options } : t
        );

        // Reset timer if duration changed
        if (
          options.duration !== undefined ||
          options.persistent !== undefined
        ) {
          const timer = timersRef.current.get(id);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(id);
          }

          const toast = updatedToasts.find((t) => t.id === id);
          if (toast) {
            const duration =
              options.duration ?? toast.duration ?? defaultDurationRef.current;
            const persistent = options.persistent ?? toast.persistent ?? false;

            if (!persistent && duration > 0) {
              const newTimer = setTimeout(() => {
                startExitAnimation(id);
                timersRef.current.delete(id);
              }, duration);
              timersRef.current.set(id, newTimer);
            }
          }
        }

        return updatedToasts;
      });
    },
    [startExitAnimation]
  );

  const contextValue = React.useMemo(
    () => ({
      showToast,
      hideToast,
      hideAllToasts,
      updateToast
    }),
    [showToast, hideToast, hideAllToasts, updateToast]
  );

  // Get all unique placements from toasts
  const allPlacements = React.useMemo(() => {
    const placements = new Set<ToastPlacement>([defaultPlacement]);
    toasts.forEach((t) => {
      if (t.placement) placements.add(t.placement);
    });
    return Array.from(placements);
  }, [toasts, defaultPlacement]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <>
            {allPlacements.map((placement) => (
              <ToastContainer
                key={placement}
                toasts={toasts}
                placement={placement}
                onClose={hideToast}
              />
            ))}
          </>,
          document.body
        )}
    </ToastContext.Provider>
  );
};

export type { Toast, ToastContextValue, ToastProviderProps };
